import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { AgGridReact } from 'ag-grid-react';
import {
  handleFilterChanged,
  handleFirstDataRendered,
  handleGridReady,
  handleItemsPerPageChange,
} from '../../common/AgGridEventHandlers';
import CustomPagination from '../../common/MotifPagination';
import ChecklistAttachmentFileCellRenderer from './customRenderer/ChecklistAttachmentFileCellRenderer';
import CustomHeaderTemplate from '../shared/customHeaders/CustomHeaderTemplate.js';
import { MotifTable } from '@ey-xd/motif-react';

const ChecklistAttachmentFileList = ({
  files,
  onDownload,
  onDelete,
  canDelete,
  emptyMessage,
  handleGridChanges,
}) => {
  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  //Default column settings
  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      minWidth: 65,
      filter: true,
      resizable: true,
      sortable: true,
      wrapText: true,
      autoHeight: true,
    };
  }, []);

  const dateOptions = useMemo(
    () => ({
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
    }),
    []
  );

  const getChecklistType = useCallback((checklistType) => {
    if (checklistType && checklistType === 1) {
      return 'ARQC';
    } else if (checklistType && checklistType === 2) {
      return 'PSQ';
    } else {
      return 'Unknown';
    }
  }, []);

  const columnDefs = useMemo(() => {
    return [
      {
        field: 'attachedFileName',
        headerName: 'File Name',
        sortable: true,
        showRowGroup: false,
        rowGroup: false,
        minWidth: 400,
        cellRenderer: ChecklistAttachmentFileCellRenderer,
        cellRendererParams: { onDownload },
      },
      {
        field: 'checklistTypeID',
        headerName: 'Checklist Type',
        sortable: true,
        showRowGroup: false,
        rowGroup: false,
        minWidth: 90,
        valueGetter: (params) => {
          return getChecklistType(params.data.checklistTypeID);
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('checklistTypeID'),
        },
      },

      {
        field: 'createdBy',
        headerName: 'Uploaded By',
        sortable: true,
        showRowGroup: false,
        rowGroup: false,
        minWidth: 275,
        headerComponentParams: { template: CustomHeaderTemplate('createdBy') },
      },
      {
        field: 'createdDateTime',
        headerName: 'Date Uploaded',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 100,
        valueGetter: (params) => {
          return params.node && params.node.data
            ? params.node.data['createdDateTime']
              ? new Date(
                  params.node.data['createdDateTime'] + 'Z'
                ).toLocaleString('en-US', dateOptions)
              : ''
            : null;
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('createdDateTime'),
        },
      },
      {
        field: 'action',
        headerName: '',
        suppressMenu: true,
        suppressHeaderFilterButton: true,
        showRowGroup: false,
        rowGroup: false,
        minWidth: 125,
        maxWidth: 125,
        cellRenderer: ChecklistAttachmentFileCellRenderer,
        cellRendererParams: { canDelete, onDownload, onDelete },
      },
    ];
  }, [dateOptions, getChecklistType, onDownload, canDelete, onDelete]);

  //Handle deselect all event
  const handleDeselectAll = useCallback((val) => {
    // set current page and total pages to 0
    setCurrentPage(0);
    setTotalPages(0);
  }, []);

  //Handle Grid Page change event
  const handlePageChange = useCallback((val) => {
    setCurrentPage(val);
    gridRef.current.api.paginationGoToPage(val - 1);
  }, []);

  useEffect(() => {
    handleGridChanges(
      rowData,
      gridRef && gridRef.current ? gridRef.current.api : null
    );
    if (rowData?.length > 0 && gridRef.current.api !== undefined) {
      let calcTotalPages = gridRef.current.api.paginationGetTotalPages();
      setTotalPages(calcTotalPages);
      //If total page is zero then set the current page as zero
      if (calcTotalPages == 0) {
        setCurrentPage(calcTotalPages);
      }
    }
  }, [rowData]);

  useEffect(() => {
    setRowData(files);
    if (gridRef && gridRef.current && gridRef.current.api) {
      // Get total pages from Grid's default pagination control and apply it to custom pagination control
      let calcTotalPages = gridRef.current.api.paginationGetTotalPages();
      setTotalPages(calcTotalPages);
    }
  }, [files]);

  const rowHeight = 50;
  const headerHeight = 50;
  const totalHeight =
    (rowData?.length <= 5 ? rowData?.length : 5) * rowHeight + headerHeight; // set 5 rows as default
  return (
    <div>
      {rowData.length > 0 && (
        <div>
          <div>
            <div className="row">
              <div className="col">
                <div
                  className="ag-theme-quartz motif-table"
                  style={{ width: '100%', height: totalHeight }}
                >
                  <MotifTable
                    zebra={true}
                    compact={true}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    accentedSort={true}
                    rowSelection="multiple"
                    suppressRowClickSelection={true}
                    onGridReady={(event) =>
                      handleGridReady(
                        event,
                        gridRef,
                        setCurrentPage,
                        setItemsPerPage,
                        setTotalPages,
                        'REITSuiteClientListColumnState',
                        handleDeselectAll
                      )
                    }
                    onFirstDataRendered={handleFirstDataRendered(gridRef)}
                    ref={gridRef}
                    pagination={true}
                    suppressPaginationPanel={true}
                    paginationPageSize={100}
                    groupDisplayType={'custom'}
                    groupSelectsChildren={true}
                    groupDefaultExpanded={0}
                    onFilterChanged={handleFilterChanged(
                      gridRef,
                      currentPage,
                      setCurrentPage,
                      setTotalPages
                    )}
                    data-testid="divFileListGrid"
                    deltaRowDataMode={true}
                  />
                  <div className="mt-3">
                    <span className="right mb-3">
                      <CustomPagination
                        currentPage={currentPage}
                        handlePageChange={handlePageChange}
                        totalPages={totalPages}
                        handleItemsPerPageChange={handleItemsPerPageChange}
                        gridRef={gridRef}
                        setCurrentPage={setCurrentPage}
                        setItemsPerPage={setItemsPerPage}
                        setTotalPages={setTotalPages}
                      />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {(files && files.length === 0 && <div>{emptyMessage}</div>) || null}
    </div>
  );
};

ChecklistAttachmentFileList.propTypes = {
  files: PropTypes.array,
  onDownload: PropTypes.func,
  onDelete: PropTypes.func,
  canDelete: PropTypes.bool,
  emptyMessage: PropTypes.string,
};

export default ChecklistAttachmentFileList;
